/**
 * Read-only экраны PWA-04 (W4): СЕГОДНЯ / ПРОГРЕСС / КАБИНЕТ участник,
 * АТЛЕТЫ + бриф тренера (Dual-Core IA §7). Данные — только Server-Side Truth
 * из `/api/v1` §6.4–6.6 (I-7 паритет с TG-ответами). Строки — только из
 * словарей `t()`/`plural()`; даты/числа — только `Intl` (I18N-02).
 */

import {
  type Checkin,
  type CoachClient,
  type CoachTask,
  type MyWallet,
  type TodayAggregate,
  type UserContext,
  confirmDeleteMyAccount,
  exportMyData,
  getCoachClients,
  getClientBrief,
  getFeatures,
  getIntegrations,
  getMeActivities,
  getMeCheckins,
  getMeContext,
  getMeProgress,
  getCoachPulse,
  getMyTasks,
  getMyWallet,
  getPrivacyConsents,
  getProgressChartPng,
  getToday,
  fetchTodayCached,
  getMeProgressMonth,
  type MonthAggregate,
  logout,
  postAdvancePhase,
  postCoachBroadcast,
  postCoachMessage,
  postCoachNote,
  postCoachTasks,
  completeCoachTask,
  createPayLink,
  revokeConnection,
  saveBodyCapture,
  setPreferences,
  setPrivacyFlags,
  setVisualConsent,
  startDeleteMyAccount,
  stravaOauthStart,
  stravaSync,
  uploadFile,
  type IntegrationConnection,
} from "./api";
import { mountGatedResult } from "./coach";
import { icon } from "./icons";
import {
  LANGS,
  fmtDate,
  fmtDateTime,
  fmtNumber,
  fmtWeight,
  getLang,
  plural,
  setLang,
  t,
  type Lang,
} from "./i18n";
import {
  disablePush,
  enablePush,
  isIosStandaloneNeeded,
  isPushSubscribed,
  registerServiceWorker,
} from "./push";

const KG = () => ` ${t("units.kg")}`;

// HTML-экранирование текстов из чужих источников (title/description заданий
// приходят от тренера) — своя иконка чата делает textContent, здесь нужен esc.
function esc(s: string): string {
  return s.replace(
    /[&<>"']/g,
    (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[
        c
      ]!,
  );
}

export function shell(className: string, html: string): HTMLElement {
  const div = document.createElement("section");
  div.className = className;
  div.innerHTML = html;
  return div;
}

function failBlock(): HTMLElement {
  return shell("screen", `<p class="muted">${t("error.generic")}</p>`);
}

// ── СЕГОДНЯ (участник) ───────────────────────────────────────────────────────
// v2.1 (UX-03..04): центр дня — один главный CTA + «Ещё сегодня» (задания
// тренера, программа, чекин) + карта пути по phases[]. Server-Side Truth.

function roadmapHtml(prog: TodayAggregate["program"]): string {
  const phases = Array.isArray(prog.phases) ? prog.phases : [];
  if (!prog.found || !phases.length) return "";
  const st = (prog.phase_state ?? {}) as Record<string, unknown>;
  const curTitle = typeof st.phase_title === "string" ? st.phase_title : "";
  const curId = typeof st.phase_id === "string" ? st.phase_id : "";
  const items = phases
    .map((p, i) => {
      const ph = p as Record<string, unknown>;
      const title = typeof ph.title === "string" ? ph.title : `#${i + 1}`;
      const isCur =
        (curTitle !== "" && title === curTitle) ||
        (curId !== "" && ph.id === curId);
      const dur = (ph.duration ?? {}) as Record<string, unknown>;
      const days = typeof dur.days === "number" ? dur.days : null;
      return `<div class="roadmap__phase${isCur ? " roadmap__phase--current" : ""}">
           <span class="roadmap__num">${i + 1}</span>
           <span class="roadmap__name">${esc(title)}</span>
           ${days !== null ? `<span class="muted">~${days} ${t("roadmap.days")}</span>` : ""}
           ${isCur ? `<span class="roadmap__here">${t("roadmap.here")}</span>` : ""}
         </div>`;
    })
    .join("");
  return `<h3 class="screen__subtitle">${t("roadmap.title")}</h3>
     <div class="card roadmap">${items}</div>`;
}

export function renderTodayScreen(
  container: HTMLElement,
  onOpenChat?: () => void,
): void {
  container.innerHTML = "";
  void (async () => {
    let data: TodayAggregate;
    try {
      data = await fetchTodayCached();
    } catch {
      container.appendChild(failBlock());
      return;
    }

    const prog = data.program ?? { found: false };
    const st = (prog.phase_state ?? {}) as Record<string, unknown>;
    const day = typeof st.day_in_phase === "number" ? st.day_in_phase : null;

    // CTA (UX-03): первая ближайшая запись дня; иначе — разговор с Никой.
    const firstUp = data.upcoming[0] ?? null;
    const ctaIsUpcoming = Boolean(
      firstUp?.fire_at && String(firstUp.fire_at).slice(0, 10) === data.date,
    );
    const ctaHtml = `<div class="card focus-card">
         <div class="muted">${t("today.cta_title")}</div>
         ${
           ctaIsUpcoming && firstUp
             ? `<div class="focus-card__what">
                  <span class="list__when">${fmtDateTime(firstUp.fire_at)}</span>
                  <span>${esc(firstUp.purpose ?? firstUp.kind)}</span>
                </div>`
             : `<div class="focus-card__what">${t("today.cta_chat")}</div>`
         }
         ${
           onOpenChat
             ? `<button class="btn focus-card__btn" id="today-cta">${t("today.cta_open")}</button>`
             : ""
         }
       </div>`;

    const programHtml = prog.found
      ? `<div class="card">
           <div class="card__title">${prog.program_title ?? ""}</div>
           <div class="muted">${t("today.phase")}: ${prog.phase ?? "—"}</div>
           ${day !== null ? `<div class="muted">${t("today.day", { n: day })}</div>` : ""}
         </div>`
      : `<p class="muted">${t("today.no_program")}</p>`;

    // Задания тренера (D6) теперь живут в «Сегодня» (UX-03), а не в «Моём».
    let tasksHtml = "";
    try {
      const tRes = await getMyTasks();
      const tasks = (tRes.tasks ?? []).filter((tk) => tk.status === "active");
      if (tasks.length) {
        tasksHtml = `<ul class="list">${tasks
          .map(
            (tk) =>
              `<li class="list__row">
                 <span class="list__what">☐ ${esc(tk.title)}</span>
                 <span class="list__when">${
                   tk.due_kind === "date" && tk.due_at
                     ? fmtDate(tk.due_at)
                     : tk.due_kind === "next_training"
                       ? t("cabinet.tasks_until_next")
                       : ""
                 }</span>
               </li>`,
          )
          .join("")}</ul>`;
      }
    } catch {
      tasksHtml = "";
    }

    const restUpcoming = data.upcoming.slice(ctaIsUpcoming ? 1 : 0);
    const upcomingHtml = restUpcoming.length
      ? `<ul class="list">${restUpcoming
          .map(
            (u) =>
              `<li class="list__row">
                 <span class="list__when">${fmtDateTime(u.fire_at)}</span>
                 <span class="list__what">${esc(u.purpose ?? u.kind)}</span>
               </li>`,
          )
          .join("")}</ul>`
      : `<p class="muted">${t("today.no_upcoming")}</p>`;

    const c = data.last_checkin;
    const checkinHtml = c
      ? `<div class="card">
           <div class="muted">${fmtDate(c.at)}</div>
           ${
             c.weight_kg !== null
               ? `<div>${fmtWeight(c.weight_kg)}${KG()}</div>`
               : ""
           }
           ${c.mood ? `<div>${esc(c.mood)}</div>` : ""}
           ${c.adherence_note ? `<div>${esc(c.adherence_note)}</div>` : ""}
           ${c.raw_text ? `<div class="muted">${esc(c.raw_text)}</div>` : ""}
         </div>`
      : `<p class="muted">${t("today.no_checkins")}</p>`;

    const view = shell(
      "screen",
      `<h2 class="screen__title">${t("today.title")}</h2>
       ${ctaHtml}
       <h3 class="screen__subtitle">${t("today.more")}</h3>
       ${tasksHtml}
       ${upcomingHtml}
       ${programHtml}
       <h3 class="screen__subtitle">${t("today.last_checkin")}</h3>
       ${checkinHtml}
       ${roadmapHtml(prog)}`,
    );
    view.querySelector<HTMLButtonElement>("#today-cta")?.addEventListener(
      "click",
      () => onOpenChat?.(),
    );
    container.appendChild(view);
  })();
}

// ── ПРОГРЕСС (участник) ──────────────────────────────────────────────────────
// v2.1 (UX-06): месяц-агрегаты — история изменения, а не дашборд метрик.
// Счёт ТОЛЬКО серверный (/me/progress/month); пустой месяц/ошибка → блок скрыт.

function monthBlock(m: MonthAggregate | null): string {
  if (!m || !m.ok) return "";
  const hasAny =
    m.weight_delta_kg !== null ||
    m.checkins > 0 ||
    m.workouts.completed > 0 ||
    m.workouts.missed > 0 ||
    m.hard_days > 0 ||
    m.returns > 0;
  if (!hasAny) return "";
  const delta =
    m.weight_delta_kg !== null
      ? `<div class="kv"><span class="kv__k">${t("progress.weight_delta")}</span>
           <span class="kv__v">${m.weight_delta_kg > 0 ? "+" : ""}${fmtWeight(m.weight_delta_kg)}${KG()}</span></div>`
      : "";
  const days = `<div class="kv"><span class="kv__k">${t("progress.checkins_days")}</span>
       <span class="kv__v">${fmtNumber(m.checkins)} · ${fmtNumber(m.active_days)}</span></div>`;
  const hard =
    m.hard_days > 0 || m.returns > 0
      ? `<div class="kv"><span class="kv__k">${t("progress.hard_returns")}</span>
           <span class="kv__v">${fmtNumber(m.hard_days)} → ${fmtNumber(m.returns)}</span></div>`
      : "";
  const due = m.workouts.completed + m.workouts.missed;
  const workouts =
    due > 0
      ? `<div class="kv"><span class="kv__k">${t("progress.workouts")}</span>
           <span class="kv__v">${fmtNumber(m.workouts.completed)} ${t("progress.workouts_of")} ${fmtNumber(due)}</span></div>`
      : "";
  return `<h3 class="screen__subtitle">${t("progress.month_title")}</h3>
     <div class="card">${delta}${days}${hard}${workouts}</div>`;
}

export function renderProgressScreen(container: HTMLElement): void {
  container.innerHTML = "";
  void (async () => {
    try {
      const [ctx, prog, chart, checkinsRes, month] = await Promise.all([
        getMeContext(),
        getMeProgress("weight"),
        getProgressChartPng("weight"),
        getMeCheckins(30),
        getMeProgressMonth(30).catch(() => null),
      ]);
      const checkins: Checkin[] = checkinsRes.checkins ?? [];
      const current = checkins.find((c) => c.weight_kg !== null)?.weight_kg ?? null;

      const weightRow = (label: string, kg: number | null | undefined) =>
        `<div class="kv"><span class="kv__k">${label}</span>
           <span class="kv__v">${kg != null ? fmtWeight(kg) + KG() : "—"}</span></div>`;

      let chartHtml = `<p class="muted">${t("progress.no_chart")}</p>`;
      if (chart) {
        const url = URL.createObjectURL(chart);
        chartHtml = `<img class="chart" src="${url}" alt="${t("progress.chart_title")}"
                          onload="URL.revokeObjectURL(this.src)">`;
      }

      const entries = checkins.length;
      const monthHtml = monthBlock(month);
      const entriesHtml = entries
        ? `<div class="muted">${plural("progress.entries", entries)}</div>
           <ul class="list">${checkins
             .slice(0, 10)
             .map(
               (c) =>
                 `<li class="list__row">
                    <span class="list__when">${fmtDate(c.at)}</span>
                    <span class="list__what">${
                      c.weight_kg !== null ? fmtWeight(c.weight_kg) + KG() : "—"
                    }</span>
                  </li>`,
             )
             .join("")}</ul>`
        : `<p class="muted">${t("progress.no_entries")}</p>`;

      container.appendChild(
        shell(
          "screen",
          `<h2 class="screen__title">${t("progress.title")}</h2>
           ${monthHtml}
           <div class="card">
             ${weightRow(t("progress.current_weight"), current)}
             ${weightRow(t("progress.start_weight"), ctx.start_weight_kg)}
             ${weightRow(t("progress.goal_weight"), ctx.goal_weight_kg)}
           </div>
           <h3 class="screen__subtitle">${t("progress.chart_title")}</h3>
           ${chartHtml}
           <h3 class="screen__subtitle">${t("progress.summary")}</h3>
           ${prog.ok && prog.summary ? `<p>${prog.summary}</p>` : ""}
           ${entriesHtml}`,
        ),
      );
    } catch {
      container.appendChild(failBlock());
    }
  })();
}

// ── КАБИНЕТ (участник и тренер) ──────────────────────────────────────────────

export function renderCabinetScreen(container: HTMLElement, onLogout?: () => void): void {
  container.innerHTML = "";
  const toolbar = document.createElement("div");
  toolbar.className = "cabinet__toolbar";
  const settingsButton = document.createElement("button");
  settingsButton.className = "chat__tool";
  settingsButton.innerHTML = icon("settings");
  settingsButton.setAttribute("aria-label", t("cabinet.settings"));
  settingsButton.setAttribute("aria-expanded", "false");
  const back = document.createElement("button");
  back.className = "linkbtn";
  back.textContent = t("cabinet.back");
  back.hidden = true;
  toolbar.append(back, settingsButton);
  const settings = document.createElement("div");
  settings.className = "cabinet__section";
  settings.hidden = true;
  settings.innerHTML = `<h2 class="screen__title">${t("cabinet.settings")}</h2>`;
  let primary = document.createElement("div");
  let scrollView: HTMLElement = container;
  const showSettings = (open: boolean): void => {
    primary.hidden = open;
    settings.hidden = !open;
    settingsButton.hidden = open;
    settingsButton.setAttribute("aria-expanded", String(open));
    back.hidden = !open;
    scrollView.scrollTop = 0;
    (open ? back : settingsButton).focus();
  };
  settingsButton.addEventListener("click", () => showSettings(true));
  back.addEventListener("click", () => showSettings(false));
  container.append(toolbar, primary, settings);
  // Выход доступен даже при ошибке или зависшей загрузке профиля.
  const exit = onLogout ? document.createElement("button") : null;
  if (exit && onLogout) {
    exit.className = "cabinet__logout";
    exit.textContent = t("logout");
    exit.addEventListener("click", () => { exit.disabled = true; onLogout(); });
    settings.appendChild(exit);
  }
  void (async () => {
    let ctx: UserContext;
    try {
      ctx = await getMeContext();
    } catch {
      primary.appendChild(failBlock());
      return;
    }

    const mem = (ctx.coach_memory ?? {}) as Record<string, unknown>;
    const privacy = (mem.privacy ?? {}) as Record<string, unknown>;

    const kv = (label: string, value: string | number | null | undefined) =>
      `<div class="kv"><span class="kv__k">${label}</span>
         <span class="kv__v">${value != null && value !== "" ? value : "—"}</span></div>`;

    const langOptions = LANGS.map(
      (l) => `<option value="${l}" ${l === getLang() ? "selected" : ""}>${t(`lang.${l}`)}</option>`,
    ).join("");

    // Visual-consent (DoD-5): ввод фото тела скрыт БЕЗ согласия — флаг
    // body_upload_visible с сервера; fail-closed и на клиенте, и в API.
    let visualGranted = false;
    try {
      const cons = await getPrivacyConsents();
      visualGranted = cons.visual.body_upload_visible;
    } catch {
      visualGranted = false;
    }

    // Задания от тренера (D6 coach_tasks, Phase 13 M2): читатель Кабинета —
    // GET /me/tasks (Server-Side Truth, паритет с tool get_my_tasks).
    let activeTasks: CoachTask[] = [];
    try {
      const tRes = await getMyTasks();
      activeTasks = (tRes.tasks ?? []).filter((tk) => tk.status === "active");
    } catch {
      activeTasks = [];
    }
    const tasksHtml = activeTasks.length
      ? `<ul class="list">${activeTasks
          .map((tk) => {
            const due =
              tk.due_kind === "date" && tk.due_at
                ? fmtDate(tk.due_at)
                : tk.due_kind === "next_training"
                  ? t("cabinet.tasks_until_next")
                  : "";
            return `<li class="list__row list__row--stack">
                <span class="list__what">${esc(tk.title)}${
                  tk.description
                    ? `<br><span class="muted">${esc(tk.description)}</span>`
                    : ""
                }</span>
                <span class="list__when">${due}</span>
              </li>`;
          })
          .join("")}</ul>`
      : `<p class="muted">${t("cabinet.tasks_empty")}</p>`;

    // Кошелёк (Phase 15 E1, §7 КАБИНЕТ «подписки/бонусы за флагами»):
    // display-only GET /me/wallet; флаги выключены → секция скрыта (DoD-9).
    let wallet: MyWallet | null = null;
    try {
      wallet = await getMyWallet();
    } catch {
      wallet = null;
    }
    const walletOn = Boolean(
      wallet && (wallet.payments_enabled || wallet.economy_enabled),
    );
    const payStatusLabels: Record<string, string> = {
      succeeded: t("cabinet.pay_succeeded"),
      refunded: t("cabinet.pay_refunded"),
      pending: t("cabinet.pay_pending"),
      failed: t("cabinet.pay_failed"),
    };
    const paymentsHtml =
      wallet?.payments_enabled && wallet.payments.length
        ? `<ul class="list">${wallet.payments
            .map(
              (p) => `<li class="list__row list__row--stack">
                 <span class="list__what">${fmtNumber(p.amount)} ${esc(p.currency)}</span>
                 <span class="list__when">${esc(payStatusLabels[p.status] ?? p.status)} · ${fmtDateTime(p.paid_at)}</span>
               </li>`,
            )
            .join("")}</ul>`
        : `<p class="muted">${t("cabinet.wallet_empty")}</p>`;
    // Подписка (ECON-04, Phase 17): статус + выгоды (биллинг v2) + оплата.
    // pay_link строит СЕРВЕР (I-7), refund-policy — verbatim-текст с сервера
    // (не LLM, PWA-09). Секция живёт только при payments_on (DoD-9/M1).
    const sub = wallet?.subscription ?? null;
    const subStateLabels: Record<string, string> = {
      active: t("cabinet.sub_active"),
      expired: t("cabinet.sub_expired"),
      never: t("cabinet.sub_never"),
      free_pilot: t("cabinet.sub_free_pilot"),
    };
    const subRows: string[] = [];
    if (sub) {
      subRows.push(
        `<div class="kv"><span class="kv__k">${t("cabinet.sub_status")}</span>
           <span class="kv__v">${esc(subStateLabels[sub.access_state] ?? sub.access_state)}</span></div>`,
      );
      if (sub.paid_until) {
        subRows.push(
          `<div class="kv"><span class="kv__k">${t("cabinet.sub_paid_until")}</span>
             <span class="kv__v">${fmtDateTime(sub.paid_until)}</span></div>`,
        );
      }
      if (sub.free_forever) {
        subRows.push(`<p class="muted">${t("cabinet.sub_free_forever")}</p>`);
      } else if (sub.free_months_remaining > 0) {
        subRows.push(
          `<div class="kv"><span class="kv__k">${t("cabinet.sub_free_months")}</span>
             <span class="kv__v">${fmtNumber(sub.free_months_remaining)}</span></div>`,
        );
      }
      if (sub.discount_percent) {
        subRows.push(
          `<div class="kv"><span class="kv__k">${t("cabinet.sub_discount")}</span>
             <span class="kv__v">${fmtNumber(sub.discount_percent)}%</span></div>`,
        );
      }
      if (sub.pay_qr_url && sub.access_state !== "free_pilot" && !sub.free_forever) {
        // РФ, самозанятый: QR СБП, сумму вводит человек; подтверждение — в боте.
        // Суммы — из offer.plans (месяц / 3 месяца / год), чтобы не гадать.
        const plans = sub.offer?.plans ?? [];
        const unit = sub.offer?.unit ?? "";
        const amounts = plans
          .map((p) => `${fmtNumber(p.price)} ${unit}`.trim())
          .filter((s) => s.length > 0)
          .join(" · ");
        const hint = amounts
          ? `${t("cabinet.sub_pay_qr_hint")} ${amounts}`
          : t("cabinet.sub_pay_qr_hint");
        subRows.push(
          `<p><img src="${esc(sub.pay_qr_url)}" alt="QR СБП" style="max-width:220px;width:100%"></p>`,
          `<p class="muted">${esc(hint)}</p>`,
        );
        if (sub.pay_link) {
          subRows.push(
            `<p><a class="btn" href="${esc(sub.pay_link)}">${t("cabinet.sub_pay_confirm")}</a></p>`,
          );
        }
      } else if (sub.pay_link && sub.access_state !== "free_pilot" && !sub.free_forever) {
        // bpcpay (мигр.055): кнопка создаёт hosted-checkout и уводит на страницу
        // банка; иначе pay_link ведёт в Telegram-бота.
        const plans = sub.offer?.plans ?? [];
        if (sub.pay_mode === "bpcpay" && plans.length > 1) {
          // Оплата оптом (owner 05.11): свой тариф на кнопку — месяц / 3 месяца / год.
          const unit = sub.offer?.unit ?? "";
          subRows.push(
            `<div class="row">${plans
              .map(
                (p) =>
                  `<button class="btn sub-pay-btn" data-plan="${esc(p.id)}">` +
                  `${esc(p.label)} — ${fmtNumber(p.price)} ${esc(unit)}</button>`,
              )
              .join("")}</div>`,
          );
        } else {
          subRows.push(
            sub.pay_mode === "bpcpay"
              ? `<p><button class="btn sub-pay-btn">${t("cabinet.sub_pay")}</button></p>`
              : `<p><a class="btn" href="${esc(sub.pay_link)}">${t("cabinet.sub_pay")}</a></p>`,
          );
        }
      }
      subRows.push(`<p class="muted">${esc(sub.refund_policy)}</p>`);
    }
    const subscriptionHtml = sub ? subRows.join("") : "";
    const bonuses = wallet?.economy_enabled ? wallet.bonuses : null;
    const bonusesHtml = bonuses?.ok
      ? `<div class="kv"><span class="kv__k">${t("cabinet.wallet_invited")}</span>
           <span class="kv__v">${fmtNumber(bonuses.invited)}</span></div>
         <div class="kv"><span class="kv__k">${t("cabinet.wallet_qualified")}</span>
           <span class="kv__v">${fmtNumber(bonuses.qualified)}</span></div>
         <div class="kv"><span class="kv__k">${t("cabinet.wallet_rate")}</span>
           <span class="kv__v">${fmtNumber(bonuses.credit_pct)}%</span></div>
         <div class="kv"><span class="kv__k">${t("cabinet.wallet_pending")}</span>
           <span class="kv__v">${fmtNumber(bonuses.credit.pending)}</span></div>
         <div class="kv"><span class="kv__k">${t("cabinet.wallet_confirmed")}</span>
           <span class="kv__v">${fmtNumber(bonuses.credit.confirmed)}</span></div>`
      : "";
    const walletHtml = walletOn
      ? `<h3 class="screen__subtitle">${t("cabinet.wallet_title")}</h3>
       <div class="card">
         ${wallet?.payments_enabled ? `${subscriptionHtml}<p class="muted">${t("cabinet.wallet_payments")}</p>${paymentsHtml}` : ""}
         ${bonuses ? `<p class="muted">${t("cabinet.wallet_bonuses")}</p>${bonusesHtml}` : ""}
       </div>`
      : "";

    const view = shell(
      "screen",
      `<h2 class="screen__title">${t("cabinet.title")}</h2>
       <h3 class="screen__subtitle">${t("cabinet.tasks_title")}</h3>
       <div class="card">${tasksHtml}</div>
       <h3 class="screen__subtitle">${t("cabinet.profile")}</h3>
       <div class="card">
         ${kv(t("cabinet.name"), esc(ctx.name ?? ""))}
         ${kv(t("cabinet.age"), ctx.age)}
         ${kv(t("cabinet.height"), ctx.height_cm)}
         ${kv(t("cabinet.goal"), esc(ctx.goal_text ?? ""))}
         ${kv(t("cabinet.intent"), esc(ctx.intent ?? ""))}
       </div>
       <h3 class="screen__subtitle">${t("cabinet.language")}</h3>
       <div class="card">
         <select class="select" id="cabinet-lang" aria-label="${t("cabinet.language")}">
           ${langOptions}
         </select>
         <p class="muted">${t("cabinet.language_hint")}</p>
       </div>
       <h3 class="screen__subtitle">${t("cabinet.privacy")}</h3>
       <div class="card">
         <p class="muted">${t("cabinet.privacy_hint")}</p>
         <label class="kv"><input type="checkbox" id="pv-achievements"
            ${privacy.share_achievements_with_coach ? "checked" : ""}>
           <span>${t("cabinet.share_achievements")}</span></label>
         <label class="kv"><input type="checkbox" id="pv-concerns"
            ${privacy.share_concerns_with_coach ? "checked" : ""}>
           <span>${t("cabinet.share_concerns")}</span></label>
         <label class="kv"><input type="checkbox" id="pv-gadget-share"
            ${privacy.share_gadget_data_with_coach ? "checked" : ""}>
           <span>${t("cabinet.share_gadget")}</span></label>
         <p class="muted">${t("cabinet.gadget_hint")}</p>
         <label class="kv"><input type="checkbox" id="pv-gadget-workouts"
            ${privacy.gadget_workouts ? "checked" : ""}>
           <span>${t("cabinet.gadget_workouts")}</span></label>
         <label class="kv"><input type="checkbox" id="pv-gadget-steps"
            ${privacy.gadget_steps ? "checked" : ""}>
           <span>${t("cabinet.gadget_steps")}</span></label>
         <label class="kv"><input type="checkbox" id="pv-gadget-weight"
            ${privacy.gadget_weight ? "checked" : ""}>
           <span>${t("cabinet.gadget_weight")}</span></label>
         <label class="kv"><input type="checkbox" id="pv-gadget-sleep"
            ${privacy.gadget_sleep ? "checked" : ""}>
           <span>${t("cabinet.gadget_sleep")}</span></label>
         <label class="kv"><input type="checkbox" id="pv-gadget-hr"
            ${privacy.gadget_hr ? "checked" : ""}>
           <span>${t("cabinet.gadget_hr")}</span></label>
         <p class="muted" id="cabinet-privacy-status"></p>
       </div>
       <h3 class="screen__subtitle">${t("cabinet.data_title")}</h3>
       <div class="card">
         <p class="muted">${t("cabinet.data_hint")}</p>
         <div class="row">
           <button class="linkbtn" id="cabinet-export">${t("cabinet.export_btn")}</button>
           <button class="linkbtn" id="cabinet-delete">${t("cabinet.delete_btn")}</button>
         </div>
         <div id="cabinet-delete-confirm" hidden>
           <p class="muted">${t("cabinet.delete_confirm_hint")} <b id="cabinet-delete-phrase-echo"></b></p>
           <div class="row">
             <input type="text" id="cabinet-delete-phrase" autocomplete="off">
             <button class="linkbtn" id="cabinet-delete-go">${t("cabinet.delete_go")}</button>
             <button class="linkbtn" id="cabinet-delete-cancel">${t("cabinet.delete_cancel")}</button>
           </div>
         </div>
         <p class="muted" id="cabinet-data-status"></p>
       </div>
       ${walletHtml}
       <h3 class="screen__subtitle">${t("cabinet.devices_title")}</h3>
       <div class="card" id="cabinet-devices">
         <p class="muted">${t("cabinet.devices_loading")}</p>
       </div>
       <h3 class="screen__subtitle">${t("cabinet.push_title")}</h3>
       <div class="card">
         <div class="row">
           <button class="linkbtn" id="cabinet-push-toggle">…</button>
         </div>
         <p class="muted" id="cabinet-push-hint"></p>
       </div>
       <h3 class="screen__subtitle">${t("cabinet.body")}</h3>
       <div class="card">
         <p class="muted">${t("cabinet.body_hint")}</p>
         <div class="row">
           <button class="linkbtn" id="cabinet-body-consent">${
             visualGranted ? t("cabinet.body_revoke") : t("cabinet.body_allow")
           }</button>
           <input type="file" id="cabinet-body-file" accept="image/*,video/*" hidden>
           <button class="linkbtn" id="cabinet-body-upload"
                   ${visualGranted ? "" : "hidden"}>${t("cabinet.body_upload")}</button>
         </div>
         <p class="muted" id="cabinet-body-status"></p>
       </div>
       <h3 class="screen__subtitle">${t("cabinet.pd")}</h3>
       <div class="card">
         <div class="kv"><span class="kv__k">${t("cabinet.pd")}</span>
           <span class="kv__v">${
             ctx.consent_at ? t("cabinet.pd_accepted") : t("cabinet.pd_required")
           }</span></div>
       </div>`,
    );
    // Существующие секции переезжают вместе с узлами: обработчики и гейты сохраняются.
    const settingsTitles = ["language", "privacy", "data_title", "devices_title", "push_title", "body", "pd"]
      .map((key) => t(`cabinet.${key}`));
    for (const heading of Array.from(view.querySelectorAll<HTMLElement>("h3"))) {
      if (!settingsTitles.includes(heading.textContent ?? "")) continue;
      const card = heading.nextElementSibling;
      settings.insertBefore(heading, exit);
      if (card) settings.insertBefore(card, exit);
    }
    const profileCard = Array.from(view.querySelectorAll<HTMLElement>("h3"))
      .find((heading) => heading.textContent === t("cabinet.profile"))?.nextElementSibling;
    if (profileCard) {
      const accountHeading = document.createElement("h3");
      accountHeading.className = "screen__subtitle";
      accountHeading.textContent = t("cabinet.profile");
      const accountCard = document.createElement("div");
      accountCard.className = "card";
      for (const row of Array.from(profileCard.children).slice(0, 3)) accountCard.append(row);
      settings.insertBefore(accountHeading, settings.children[1] ?? exit);
      accountHeading.after(accountCard);
      profileCard.previousElementSibling!.textContent = t("cabinet.goal");
    }
    const content = document.createElement("div");
    content.className = "cabinet__section";
    content.append(...Array.from(view.childNodes));
    // querySelector(view) продолжает видеть и настройки.
    view.append(content, settings);
    content.hidden = settings.hidden === false;
    primary.replaceWith(view);
    primary = content;
    scrollView = view;

    const program = document.createElement("section");
    program.innerHTML = `<h3 class="screen__subtitle">${t("brief.program")}</h3>`;
    content.querySelector("h2")?.after(program);
    void getToday().then((today) => {
      const card = document.createElement("div");
      card.className = "card";
      card.textContent = today.program.found
        ? [today.program.program_title, today.program.phase].filter(Boolean).join(" · ")
        : t("today.no_program");
      program.append(card);
    }).catch(() => program.append(failBlock()));

    // ── Фото тела: visual-consent grant/revoke + загрузка (DoD-5) ───────────
    const consentBtn = view.querySelector<HTMLButtonElement>("#cabinet-body-consent")!;
    const uploadBtn = view.querySelector<HTMLButtonElement>("#cabinet-body-upload")!;
    const fileInput = view.querySelector<HTMLInputElement>("#cabinet-body-file")!;
    const bodyStatus = view.querySelector<HTMLElement>("#cabinet-body-status")!;

    const setVisualState = (granted: boolean) => {
      visualGranted = granted;
      consentBtn.textContent = granted
        ? t("cabinet.body_revoke")
        : t("cabinet.body_allow");
      uploadBtn.hidden = !granted;
    };

    consentBtn.addEventListener("click", () =>
      void (async () => {
        consentBtn.disabled = true;
        try {
          const res = await setVisualConsent(visualGranted ? "revoke" : "grant");
          setVisualState(res.consent);
          bodyStatus.textContent = visualGranted
            ? t("cabinet.body_allowed")
            : `${t("cabinet.body_revoked")}${
                res.body_photos_purged
                  ? ` (${t("cabinet.body_purged", {
                      n: String(res.body_photos_purged),
                    })})`
                  : ""
              }`;
        } catch {
          bodyStatus.textContent = t("error.generic");
        } finally {
          consentBtn.disabled = false;
        }
      })(),
    );

    uploadBtn.addEventListener("click", () => fileInput.click());
    fileInput.addEventListener("change", () => {
      const file = fileInput.files?.[0];
      fileInput.value = "";
      if (!file) return;
      void (async () => {
        uploadBtn.disabled = true;
        bodyStatus.textContent = t("cabinet.body_uploading");
        try {
          const res = await uploadFile(file, "body_progress");
          // §6.7: точка трансформации из PWA — сервер кладёт media_id в meta.
          await saveBodyCapture(res.media.media_id, file.name);
          bodyStatus.textContent = t("cabinet.body_saved");
        } catch {
          bodyStatus.textContent = visualGranted
            ? t("error.generic")
            : t("cabinet.body_hidden");
        } finally {
          uploadBtn.disabled = false;
        }
      })();
    });

    // Оплата (мигр.055): hosted-checkout PayPro по кнопке из кошелька.
    view.querySelectorAll<HTMLButtonElement>(".sub-pay-btn").forEach((btn) =>
      btn.addEventListener("click", (ev) => {
        const btn = ev.currentTarget as HTMLButtonElement;
        btn.disabled = true;
        void (async () => {
          try {
            // Возврат после оплаты — обратно в PWA (?r=ok|fail|decline|cancel),
            // а не на сайт (owner 04.11: кнопка «в ТГ» в приложении не нужна).
            const res = await createPayLink(window.location.origin + "/",
              btn.dataset.plan);
            const url = res.ok && res.url ? res.url : res.fallback_url;
            if (url) {
              window.location.href = url;
              return;
            }
          } catch {
            /* показываем кнопку снова */
          }
          btn.disabled = false;
        })();
      }),
    );

    view.querySelector<HTMLSelectElement>("#cabinet-lang")!
      .addEventListener("change", (ev) => {
        const lang = (ev.target as HTMLSelectElement).value as Lang;
        setLang(lang); // localStorage + <html lang>
        // выбор сохраняется и в профиле (§7.1); reload — честное применение
        // языка ко всему UI без остатка (DoD-11)
        void setPreferences(lang)
          .catch(() => undefined)
          .then(() => location.reload());
      });

    // ── Web Push (W3, §11): включение/выключение + iOS-гайд ─────────────────
    registerServiceWorker();
    const pushBtn = view.querySelector<HTMLButtonElement>("#cabinet-push-toggle")!;
    const pushHint = view.querySelector<HTMLElement>("#cabinet-push-hint")!;

    const setPushState = (on: boolean, hint: string) => {
      pushBtn.textContent = on
        ? t("cabinet.push_disable")
        : t("cabinet.push_enable");
      pushBtn.disabled = false;
      pushHint.textContent = hint;
    };

    void (async () => {
      if (isIosStandaloneNeeded()) {
        // §11: iOS push — только standalone → гайд «Добавить на экран Домой».
        pushBtn.hidden = true;
        pushHint.textContent = t("cabinet.push_ios_guide");
        return;
      }
      const on = await isPushSubscribed().catch(() => false);
      setPushState(on, on ? t("cabinet.push_enabled") : t("cabinet.push_disabled"));
    })();

    pushBtn.addEventListener("click", () =>
      void (async () => {
        pushBtn.disabled = true;
        pushHint.textContent = t("cabinet.push_working");
        try {
          if (await isPushSubscribed().catch(() => false)) {
            await disablePush();
            setPushState(false, t("cabinet.push_disabled"));
            return;
          }
          const result = await enablePush();
          if (result === "subscribed") {
            setPushState(true, t("cabinet.push_enabled"));
          } else if (result === "needs_ios_install") {
            pushBtn.hidden = true;
            pushHint.textContent = t("cabinet.push_ios_guide");
          } else if (result === "denied") {
            setPushState(false, t("cabinet.push_denied"));
          } else if (result === "disabled") {
            setPushState(false, t("cabinet.push_unavailable"));
          } else if (result === "unsupported") {
            setPushState(false, t("cabinet.push_unsupported"));
          } else {
            setPushState(false, t("error.generic"));
          }
        } catch {
          setPushState(false, t("error.generic"));
        }
      })(),
    );

    // ── Privacy-тумблеры (W6, GAD-08): категории гаджетов + «передавать
    // тренеру» — default OFF, каждая категория независимо (I-8/OD-7) ────────
    const privacyStatus =
      view.querySelector<HTMLElement>("#cabinet-privacy-status")!;
    const privacyBoxes: Array<[string, string]> = [
      ["pv-achievements", "share_achievements_with_coach"],
      ["pv-concerns", "share_concerns_with_coach"],
      ["pv-gadget-share", "share_gadget_data_with_coach"],
      ["pv-gadget-workouts", "gadget_workouts"],
      ["pv-gadget-steps", "gadget_steps"],
      ["pv-gadget-weight", "gadget_weight"],
      ["pv-gadget-sleep", "gadget_sleep"],
      ["pv-gadget-hr", "gadget_hr"],
    ];
    for (const [boxId, flagKey] of privacyBoxes) {
      view
        .querySelector<HTMLInputElement>(`#${boxId}`)!
        .addEventListener("change", (ev) => {
          const input = ev.target as HTMLInputElement;
          void (async () => {
            try {
              await setPrivacyFlags({ [flagKey]: input.checked });
              privacyStatus.textContent = t("cabinet.privacy_saved");
            } catch {
              input.checked = !input.checked; // откат честного UI
              privacyStatus.textContent = t("cabinet.privacy_save_error");
            }
          })();
        });
    }

    // ── Устройства и приложения (W6, PWA-08): источники данных ──────────────
    void renderDevices(view);

    // ── Мои данные: экспорт + двухшаговое удаление (E2, DoD-8) ─────────────
    const dataStatus = view.querySelector<HTMLElement>("#cabinet-data-status")!;
    const exportBtn = view.querySelector<HTMLButtonElement>("#cabinet-export")!;
    const deleteBtn = view.querySelector<HTMLButtonElement>("#cabinet-delete")!;
    const confirmBox = view.querySelector<HTMLElement>(
      "#cabinet-delete-confirm",
    )!;
    const phraseEcho = view.querySelector<HTMLElement>(
      "#cabinet-delete-phrase-echo",
    )!;
    const phraseInput =
      view.querySelector<HTMLInputElement>("#cabinet-delete-phrase")!;
    const deleteGo = view.querySelector<HTMLButtonElement>("#cabinet-delete-go")!;
    const deleteCancel =
      view.querySelector<HTMLButtonElement>("#cabinet-delete-cancel")!;

    exportBtn.addEventListener("click", () =>
      void (async () => {
        exportBtn.disabled = true;
        dataStatus.textContent = t("cabinet.data_working");
        try {
          const data = await exportMyData();
          const blob = new Blob([JSON.stringify(data, null, 2)], {
            type: "application/json",
          });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = "nika-my-data.json";
          a.click();
          URL.revokeObjectURL(url);
          dataStatus.textContent = "";
        } catch {
          dataStatus.textContent = t("error.generic");
        } finally {
          exportBtn.disabled = false;
        }
      })(),
    );

    deleteBtn.addEventListener("click", () =>
      void (async () => {
        deleteBtn.disabled = true;
        dataStatus.textContent = t("cabinet.data_working");
        try {
          const step = await startDeleteMyAccount();
          if (step.ok && step.confirmation_required) {
            phraseEcho.textContent = step.confirm_phrase ?? "";
            phraseInput.value = "";
            confirmBox.hidden = false;
            dataStatus.textContent = "";
            phraseInput.focus();
          } else {
            dataStatus.textContent = t("cabinet.delete_error");
          }
        } catch {
          dataStatus.textContent = t("cabinet.delete_error");
        } finally {
          deleteBtn.disabled = false;
        }
      })(),
    );

    deleteCancel.addEventListener("click", () => {
      confirmBox.hidden = true;
      phraseInput.value = "";
      dataStatus.textContent = "";
    });

    deleteGo.addEventListener("click", () =>
      void (async () => {
        deleteGo.disabled = true;
        dataStatus.textContent = t("cabinet.data_working");
        try {
          const res = await confirmDeleteMyAccount(phraseInput.value);
          if (res.ok) {
            confirmBox.hidden = true;
            dataStatus.textContent = t("cabinet.delete_done");
            logout();
            setTimeout(() => location.reload(), 2000);
          } else if (res.error === "confirm_phrase_mismatch") {
            // окно подтверждения живо — можно вводить фразу повторно
            dataStatus.textContent = t("cabinet.delete_wrong_phrase");
          } else {
            // окно истекло (confirmation_not_acknowledged) — начни заново
            confirmBox.hidden = true;
            dataStatus.textContent = t("cabinet.delete_error");
          }
        } catch {
          dataStatus.textContent = t("cabinet.delete_error");
        } finally {
          deleteGo.disabled = false;
        }
      })(),
    );
  })();
}

// ── Устройства и приложения (W6, PWA-08): источники данных ─────────────────
// Честные карточки (§10.6): только реально доступные варианты, никаких кнопок
// к неработающему. Данные — канонические события без знания о провайдерах
// (§10.9); отзыв доступа = стоп + удаление данных источника (GAD-08).

async function renderDevices(view: HTMLElement, message?: string): Promise<void> {
  const box = view.querySelector<HTMLElement>("#cabinet-devices");
  if (!box) return;

  // Статус возврата из OAuth (?strava=…) — показать один раз и вычистить URL.
  let note = message ?? "";
  const params = new URLSearchParams(location.search);
  const oauth = params.get("strava");
  if (oauth) {
    note =
      oauth === "connected"
        ? t("cabinet.devices_oauth_connected")
        : oauth === "denied"
          ? t("cabinet.devices_oauth_denied")
          : t("cabinet.devices_oauth_error");
    params.delete("strava");
    params.delete("reason");
    const qs = params.toString();
    history.replaceState(null, "", location.pathname + (qs ? `?${qs}` : ""));
  }

  let features: Awaited<ReturnType<typeof getFeatures>>;
  try {
    features = await getFeatures();
  } catch {
    box.innerHTML = `<p class="muted">${t("error.generic")}</p>`;
    return;
  }
  // §10.6 честное обещание: контур выключен → «Coming Soon», без кнопок
  if (!features.flags.WEARABLES_INTEGRATION?.enabled) {
    box.innerHTML = `<p class="muted">${t("cabinet.devices_coming_soon")}</p>
      <p class="muted">${t("cabinet.devices_coming_hint")}</p>`;
    return;
  }

  let connections: IntegrationConnection[] = [];
  try {
    connections = (await getIntegrations()).connections ?? [];
  } catch {
    connections = [];
  }
  const strava = connections.find((c) => c.provider === "strava");

  // Управление импортированными данными: честный счётчик канонических событий
  let importedCount: number | null = null;
  if (strava) {
    try {
      importedCount = ((await getMeActivities(500)).events ?? []).length;
    } catch {
      importedCount = null;
    }
  }

  const statusLabel = (c: IntegrationConnection): string =>
    c.status === "active"
      ? t("cabinet.devices_connected")
      : c.status === "revoked"
        ? t("cabinet.devices_status_revoked")
        : c.status === "expired"
          ? t("cabinet.devices_status_expired")
          : t("cabinet.devices_status_error");

  const stravaHtml = strava
    ? `<p><b>${t("cabinet.devices_strava")}</b> — ${statusLabel(strava)}</p>
       <div class="kv"><span class="kv__k">${t("cabinet.devices_last_sync")}</span>
         <span class="kv__v">${
           strava.last_sync_at
             ? fmtDateTime(strava.last_sync_at)
             : t("cabinet.devices_never")
         }</span></div>
       <p class="muted">${t("cabinet.devices_strava_categories")}</p>
       <p class="muted">${t("cabinet.devices_strava_missing")}</p>
       <p class="muted">${t("cabinet.devices_strava_first_load")}</p>
       <p class="muted">${t("cabinet.devices_imported")}: ${
         importedCount != null ? fmtNumber(importedCount) : "—"
       }</p>
       <div class="row">
         <button class="linkbtn" id="dev-sync">${t("cabinet.devices_sync")}</button>
         <button class="linkbtn" id="dev-revoke">${t("cabinet.devices_revoke")}</button>
       </div>
       <div id="dev-revoke-confirm" hidden>
         <p class="muted">${t("cabinet.devices_revoke_confirm")}</p>
         <div class="row">
           <button class="linkbtn" id="dev-revoke-yes">${t("cabinet.devices_revoke_yes")}</button>
           <button class="linkbtn" id="dev-revoke-no">${t("cabinet.delete_cancel")}</button>
         </div>
       </div>`
    : `<p><b>${t("cabinet.devices_strava")}</b></p>
       <p class="muted">${t("cabinet.devices_strava_about")}</p>
       <p class="muted">${t("cabinet.devices_strava_categories")}</p>
       <p class="muted">${t("cabinet.devices_strava_missing")}</p>
       <p class="muted">${t("cabinet.devices_strava_first_load")}</p>
       <div class="row">
         <button class="linkbtn" id="dev-connect">${t("cabinet.devices_connect")}</button>
       </div>`;

  box.innerHTML = `${note ? `<p class="muted" id="dev-note">${esc(note)}</p>` : ""}
    ${stravaHtml}
    <p class="muted">${t("cabinet.devices_imported_hint")}</p>
    <p><b>${t("cabinet.devices_healthkit")}</b></p>
    <p class="muted">${t("cabinet.devices_healthkit_hint")}</p>
    <p><b>${t("cabinet.devices_healthconnect")}</b></p>
    <p class="muted">${t("cabinet.devices_healthconnect_hint")}</p>
    <p class="muted" id="dev-status"></p>`;

  const status = box.querySelector<HTMLElement>("#dev-status")!;

  box.querySelector<HTMLButtonElement>("#dev-connect")?.addEventListener(
    "click",
    () =>
      void (async () => {
        try {
          const res = await stravaOauthStart();
          if (res.ok && res.url) {
            location.assign(res.url); // OAuth-редирект на Strava (state серверный)
          } else {
            status.textContent = t("cabinet.devices_oauth_error");
          }
        } catch {
          status.textContent = t("cabinet.devices_oauth_error");
        }
      })(),
  );

  box.querySelector<HTMLButtonElement>("#dev-sync")?.addEventListener(
    "click",
    (ev) =>
      void (async () => {
        const btn = ev.target as HTMLButtonElement;
        btn.disabled = true;
        status.textContent = t("cabinet.devices_syncing");
        try {
          const res = await stravaSync();
          await renderDevices(
            view,
            res.ok
              ? res.rate_limited
                ? t("cabinet.devices_sync_rate_limited")
                : t("cabinet.devices_synced", {
                    n: String(res.inserted ?? 0),
                  })
              : t("error.generic"),
          );
        } catch {
          status.textContent = t("error.generic");
          btn.disabled = false;
        }
      })(),
  );

  // Отключение — двухшагово (данные источника удаляются): без спешки
  box.querySelector<HTMLButtonElement>("#dev-revoke")?.addEventListener(
    "click",
    () => {
      const confirmBox = box.querySelector<HTMLElement>("#dev-revoke-confirm")!;
      confirmBox.hidden = false;
    },
  );
  box.querySelector<HTMLButtonElement>("#dev-revoke-no")?.addEventListener(
    "click",
    () => {
      const confirmBox = box.querySelector<HTMLElement>("#dev-revoke-confirm")!;
      confirmBox.hidden = true;
    },
  );
  box.querySelector<HTMLButtonElement>("#dev-revoke-yes")?.addEventListener(
    "click",
    () =>
      void (async () => {
        if (!strava) return;
        status.textContent = t("cabinet.devices_revoking");
        try {
          const res = await revokeConnection(strava.id);
          await renderDevices(
            view,
            res.ok
              ? `${t("cabinet.devices_revoked")} (${t("cabinet.devices_imported")}: ${fmtNumber(res.events_purged ?? 0)})`
              : t("error.generic"),
          );
        } catch {
          status.textContent = t("error.generic");
        }
      })(),
  );
}

// ── АТЛЕТЫ (тренер) ─────────────────────────────────────────────────────────

export function renderAthletesScreen(
  container: HTMLElement,
  onOpenBrief: (clientTg: number) => void,
): void {
  container.innerHTML = "";
  void (async () => {
    let res: { ok?: boolean; clients?: CoachClient[]; error?: string };
    try {
      res = await getCoachClients();
    } catch {
      container.appendChild(failBlock());
      return;
    }
    const clients = res.clients ?? [];
    // v2.1 Phase 23 (UX-12): desktop ≥1024px — master-detail (список слева,
    // бриф в центре, «Далее сегодня» справа); мобильный flow не меняется.
    const wide = window.matchMedia("(min-width: 1024px)").matches;
    const master = document.createElement("div");
    master.className = "split__master";
    const detail = document.createElement("div");
    detail.className = "split__detail";
    const side = document.createElement("div");
    side.className = "split__side";
    const openClient = (tg: number): void => {
      if (wide) {
        detail.innerHTML = "";
        renderBriefScreen(detail, tg, () => {
          detail.innerHTML = "";
        });
        detail.querySelector<HTMLElement>(".screen")?.scrollTo(0, 0);
      } else {
        onOpenBrief(tg);
      }
    };

    const header = shell(
      "screen",
      `<h2 class="screen__title">${t("athletes.title")}</h2>
       <div class="muted">${plural("athletes.count", clients.length)}</div>`,
    );
    master.appendChild(header);

    if (res.ok === false) {
      master.appendChild(
        shell("screen", `<p class="muted">${t("athletes.error")}</p>`),
      );
      container.appendChild(master);
      return;
    }
    if (!clients.length) {
      master.appendChild(
        shell("screen", `<p class="muted">${t("athletes.empty")}</p>`),
      );
      container.appendChild(master);
      return;
    }

    const list = shell(
      "screen",
      `<ul class="list">${clients
        .map(
          (c) =>
            `<li class="list__row list__row--action" data-tg="${c.client_tg_id}">
               <span class="list__what">${c.name ?? c.client_tg_id}</span>
               <span class="list__when">${
                 c.started_at
                   ? t("athletes.since", { date: fmtDate(c.started_at) })
                   : ""
               }</span>
             </li>`,
        )
        .join("")}</ul>`,
    );
    list.querySelectorAll<HTMLElement>(".list__row--action").forEach((row) => {
      row.addEventListener("click", () =>
        openClient(Number(row.dataset.tg)));
    });
    master.appendChild(list);

    // Рассылка клиентам (P2, §8 kind=coach_broadcast): первый вызов —
    // карточка черновика, текст уходит ДОСЛОВНО после «Подтвердить».
    const bc = shell(
      "screen",
      `<h3 class="screen__subtitle">${t("athletes.broadcast")}</h3>
       <div class="card">
         <div class="form">
           <label class="form__label">${t("athletes.broadcast_segment")}
             <select class="select js-bc-segment">
               <option value="all_active">${t("athletes.segment.all_active")}</option>
               <option value="training_tomorrow">${t("athletes.segment.training_tomorrow")}</option>
               <option value="lapsed_checkins">${t("athletes.segment.lapsed_checkins")}</option>
               <option value="missed_checkins">${t("athletes.segment.missed_checkins")}</option>
               <option value="no_checkins_days">${t("athletes.segment.no_checkins_days")}</option>
               <option value="new_clients_week">${t("athletes.segment.new_clients_week")}</option>
               <option value="program_active">${t("athletes.segment.program_active")}</option>
             </select></label>
           <label class="form__label">${t("athletes.broadcast_text")}
             <textarea class="form__textarea js-bc-text" rows="3"></textarea></label>
           <button class="form__btn js-bc-send">${t("athletes.broadcast_send")}</button>
           <div class="form__status js-bc-status"></div>
         </div>
       </div>`,
    );
    master.appendChild(bc);
    const bcStatus = bc.querySelector<HTMLElement>(".js-bc-status")!;
    bc.querySelector<HTMLButtonElement>(".js-bc-send")!
      .addEventListener("click", () => {
        const segment =
          bc.querySelector<HTMLSelectElement>(".js-bc-segment")!.value;
        const text =
          bc.querySelector<HTMLTextAreaElement>(".js-bc-text")!.value.trim();
        if (!text) {
          bcStatus.textContent = t("error.generic");
          return;
        }
        void (async () => {
          try {
            const res = await postCoachBroadcast(segment, text);
            mountGatedResult(bcStatus, res);
          } catch {
            bcStatus.textContent = t("error.generic");
          }
        })();
      });

    if (!wide) {
      container.appendChild(master);
      return;
    }
    // Desktop-развёртка: список / контекст / «Далее сегодня» (события дня).
    container.appendChild(master);
    container.appendChild(detail);
    container.appendChild(side);
    void getCoachPulse()
      .then((pd) => {
        if (!pd.ok) return;
        side.appendChild(
          shell(
            "screen",
            `<h3 class="screen__subtitle">${t("pulse.events")}</h3>
             ${
               pd.events.length
                 ? `<ul class="list">${pd.events
                     .map(
                       (e) =>
                         `<li class="list__row">
                            <span class="list__when">${e.hhmm}</span>
                            <span class="list__what">${e.client_name ?? ""}${
                              e.activity ? ` <span class="muted">${e.activity}</span>` : ""
                            }</span>
                          </li>`,
                     )
                     .join("")}</ul>`
                 : `<p class="muted">${t("pulse.no_events")}</p>`
             }`,
          ),
        );
      })
      .catch(() => {
        /* без событий — колонка остаётся пустой */
      });
  })();
}

// ── Бриф клиента (тренер, I-8: recovery по consent-флагу) ────────────────────

export function renderBriefScreen(
  container: HTMLElement,
  clientTg: number,
  onBack: () => void,
): void {
  container.innerHTML = "";
  void (async () => {
    let data: Record<string, unknown>;
    try {
      data = await getClientBrief(clientTg);
    } catch {
      container.appendChild(failBlock());
      return;
    }
    const client = (data.client ?? {}) as Record<string, unknown>;
    const slice = (client.program_slice ?? {}) as Record<string, unknown>;
    const checkins = (data.recent_checkins ?? []) as Array<
      Record<string, unknown>
    >;
    const notes = (data.recent_coach_notes ?? []) as Array<
      Record<string, unknown>
    >;
    const recovery = data.recovery as Record<string, unknown> | null | undefined;
    const recoveryNote = data.recovery_note as string | null | undefined;
    // Задания/инструкции (D6 coach_tasks): активные first + последние закрытые.
    const tasks = (data.tasks ?? []) as Array<Record<string, unknown>>;

    const kv = (label: string, value: unknown) =>
      `<div class="kv"><span class="kv__k">${label}</span>
         <span class="kv__v">${
           value != null && value !== "" ? String(value) : "—"
         }</span></div>`;

    const programHtml = client.program_title || slice.title
      ? `<div class="card__title">${String(client.program_title ?? slice.title)}</div>
         ${kv(t("today.phase"), slice.phase ?? client.phase)}
         ${
           typeof slice.day_in_phase === "number"
             ? `<div class="muted">${t("today.day", { n: slice.day_in_phase })}</div>`
             : ""
         }`
      : `<p class="muted">${t("today.no_program")}</p>`;

    let recoveryHtml = "";
    if (recovery) {
      recoveryHtml = `
        <h3 class="screen__subtitle">${t("brief.recovery")}</h3>
        <div class="card">
          ${recovery.lapse_text ? `<div>${String(recovery.lapse_text)}</div>` : ""}
          <div class="muted">${fmtDateTime(
            (recovery.started_at as string) ?? null,
          )}</div>
        </div>`;
    } else if (recoveryNote) {
      recoveryHtml = `
        <h3 class="screen__subtitle">${t("brief.recovery")}</h3>
        <p class="muted">${t("brief.recovery_hidden")}</p>`;
    }

    // v2.1 Phase 22 (UX-08): бриф-360 — месяц-агрегат клиента (серверный
    // счёт; returns маскируется сервером без consent) + следующая тренировка.
    const bm = (data.month ?? null) as Record<string, unknown> | null;
    let briefMonthHtml = "";
    if (bm && bm.ok) {
      const rows: string[] = [];
      if (typeof bm.weight_delta_kg === "number") {
        const d = bm.weight_delta_kg as number;
        rows.push(`<div class="kv"><span class="kv__k">${t("progress.weight_delta")}</span>
           <span class="kv__v">${d > 0 ? "+" : ""}${fmtWeight(d)}${KG()}</span></div>`);
      }
      rows.push(`<div class="kv"><span class="kv__k">${t("progress.checkins_days")}</span>
         <span class="kv__v">${fmtNumber(Number(bm.checkins ?? 0))} · ${fmtNumber(Number(bm.active_days ?? 0))}</span></div>`);
      const hd = Number(bm.hard_days ?? 0);
      const rt = bm.returns;
      if (hd > 0 || (typeof rt === "number" && rt > 0)) {
        rows.push(`<div class="kv"><span class="kv__k">${t("progress.hard_returns")}</span>
           <span class="kv__v">${fmtNumber(hd)} → ${typeof rt === "number" ? fmtNumber(rt) : "—"}</span></div>`);
      }
      const w = (bm.workouts ?? {}) as Record<string, unknown>;
      const done = Number(w.completed ?? 0);
      const missed = Number(w.missed ?? 0);
      if (done + missed > 0) {
        rows.push(`<div class="kv"><span class="kv__k">${t("progress.workouts")}</span>
           <span class="kv__v">${fmtNumber(done)} ${t("progress.workouts_of")} ${fmtNumber(done + missed)}</span></div>`);
      }
      if (rows.length) {
        briefMonthHtml = `<h3 class="screen__subtitle">${t("progress.month_title")}</h3>
           <div class="card">${rows.join("")}</div>`;
      }
    }
    const nt = (data.next_training ?? null) as Record<string, unknown> | null;
    const nextTrainingHtml = nt
      ? `<div class="kv"><span class="kv__k">${t("brief.next_training")}</span>
           <span class="kv__v">${fmtDateTime(String(nt.start_at ?? ""))}${nt.activity ? ` · ${esc(String(nt.activity))}` : ""}</span></div>`
      : "";

    container.appendChild(
      shell(
        "screen",
        `<button class="linkbtn" id="brief-back">${t("brief.back")}</button>
         <h2 class="screen__title">${t("brief.title")} — ${client.name ?? clientTg}</h2>
         <h3 class="screen__subtitle">${t("brief.profile")}</h3>
         <div class="card">
           ${kv(t("cabinet.name"), client.name)}
           ${kv(t("cabinet.goal"), client.goal_text)}
           ${kv(t("progress.start_weight"), client.start_weight_kg != null ? fmtWeight(Number(client.start_weight_kg)) + KG() : null)}
           ${kv(t("progress.goal_weight"), client.goal_weight_kg != null ? fmtWeight(Number(client.goal_weight_kg)) + KG() : null)}
           ${kv(t("brief.messages_7d"), client.messages_last_7d)}
           ${nextTrainingHtml}
         </div>
         <h3 class="screen__subtitle">${t("brief.program")}</h3>
         <div class="card">${programHtml}</div>
         ${briefMonthHtml}
         <h3 class="screen__subtitle">${t("brief.checkins")}</h3>
         ${
           checkins.length
             ? `<ul class="list">${checkins
                 .map(
                   (c) =>
                     `<li class="list__row">
                        <span class="list__when">${fmtDate((c.at as string) ?? null)}</span>
                        <span class="list__what">${
                          c.weight_kg != null
                            ? fmtWeight(Number(c.weight_kg)) + KG()
                            : "—"
                        }</span>
                      </li>`,
                 )
                 .join("")}</ul>`
             : `<p class="muted">${t("brief.no_data")}</p>`
         }
         ${recoveryHtml}
         <h3 class="screen__subtitle">${t("brief.notes")}</h3>
         ${
           notes.length
             ? `<ul class="list">${notes
                 .map(
                   (n) =>
                     `<li class="list__row list__row--stack">
                        <span class="list__when">${fmtDate((n.created_at as string) ?? null)}</span>
                        <span class="list__what">${String(n.content ?? "")}</span>
                      </li>`,
                 )
                 .join("")}</ul>`
             : `<p class="muted">${t("brief.no_data")}</p>`
         }
         <h3 class="screen__subtitle">${t("brief.tasks")}</h3>
         ${
           tasks.length
             ? `<ul class="list">${tasks
                 .map((tk) => {
                   const due =
                     tk.due_kind === "date" && tk.due_at
                       ? fmtDate(String(tk.due_at))
                       : tk.due_kind === "next_training"
                         ? t("cabinet.tasks_until_next")
                         : "";
                   const st =
                     tk.status === "done"
                       ? t("brief.task_done")
                       : tk.status === "cancelled"
                         ? t("brief.task_cancelled")
                         : "";
                   return `<li class="list__row list__row--stack">
                       <span class="list__what">${esc(String(tk.title ?? ""))}${
                         tk.description
                           ? `<br><span class="muted">${esc(String(tk.description))}</span>`
                           : ""
                       }</span>
                       <span class="list__when">${
                         [st, due].filter(Boolean).join(" · ")
                       }</span>
                       ${
                         tk.status === "active"
                           ? `<span class="row">
                                <button class="linkbtn js-task-done"
                                        data-task="${tk.task_id}">${t("brief.task_done")}</button>
                                <button class="linkbtn js-task-cancel"
                                        data-task="${tk.task_id}">${t("brief.task_cancel")}</button>
                              </span>`
                           : ""
                       }
                     </li>`;
                 })
                 .join("")}</ul>`
             : `<p class="muted">${t("brief.no_data")}</p>`
         }
         <h3 class="screen__subtitle">${t("brief.actions")}</h3>
         <div class="card">
           <div class="form">
             <label class="form__label">${t("brief.message_label")}
               <textarea class="form__textarea js-msg-text" rows="3"></textarea></label>
             <button class="form__btn js-msg-send">${t("brief.message_send")}</button>
             <div class="form__status js-msg-status"></div>
           </div>
         </div>
         ${
           slice.assignment_id
             ? `<div class="card">
                  <div class="form">
                    <label class="form__label">${t("programs.advance_hint")}
                      <input class="form__input js-adv-phase" type="text"></label>
                    <button class="form__btn js-adv-run">${t("programs.advance")}</button>
                    <div class="form__status js-adv-status"></div>
                  </div>
                </div>`
             : ""
         }
         <div class="card">
           <div class="form">
             <label class="form__label">${t("brief.note_label")}
               <textarea class="form__textarea js-note-text" rows="2"></textarea></label>
             <label class="form__label">${t("brief.note_type")}
               <select class="select js-note-type">
                 <option value="observation">${t("brief.note_type.observation")}</option>
                 <option value="decision">${t("brief.note_type.decision")}</option>
                 <option value="instruction">${t("brief.note_type.instruction")}</option>
                 <option value="general">${t("brief.note_type.general")}</option>
               </select></label>
             <label class="form__label">${t("brief.note_visibility")}
               <select class="select js-note-visibility">
                 <option value="coach_private">${t("brief.visibility.coach_private")}</option>
                 <option value="user_instruction">${t("brief.visibility.user_instruction")}</option>
                 <option value="user_summary">${t("brief.visibility.user_summary")}</option>
               </select></label>
             <button class="form__btn js-note-save">${t("brief.note_save")}</button>
             <div class="form__status js-note-status"></div>
           </div>
         </div>
         <div class="card">
           <div class="form">
             <label class="form__label">${t("brief.task_new")}
               <input class="form__input js-task-title" type="text"
                      placeholder="${t("brief.task_title_ph")}"></label>
             <label class="form__label">${t("brief.task_desc_ph")}
               <input class="form__input js-task-desc" type="text"></label>
             <label class="form__label">${t("brief.task_due")}
               <select class="select js-task-due">
                 <option value="next_training">${t("cabinet.tasks_until_next")}</option>
                 <option value="date">${t("brief.task_due_date")}</option>
               </select></label>
             <label class="form__label">${t("brief.task_due_date")}
               <input class="form__input js-task-date" type="datetime-local"></label>
             <button class="form__btn js-task-save">${t("brief.task_save")}</button>
             <div class="form__status js-task-status"></div>
           </div>
         </div>`,
      ),
    );
    container.querySelector("#brief-back")!.addEventListener("click", onBack);

    // ── Действия (P2): сообщение/фаза — через Confirm-Gate (§8); ─────────────
    // заметка/задание — данные, вне гейта (M2, статус-кво Phase 13).
    const rerender = () => renderBriefScreen(container, clientTg, onBack);
    const assignmentId = Number(slice.assignment_id ?? 0) || null;

    container.querySelector<HTMLButtonElement>(".js-msg-send")!
      .addEventListener("click", () => {
        const text =
          container.querySelector<HTMLTextAreaElement>(".js-msg-text")!.value.trim();
        const status = container.querySelector<HTMLElement>(".js-msg-status")!;
        if (!text) {
          status.textContent = t("error.generic");
          return;
        }
        void (async () => {
          try {
            const res = await postCoachMessage(clientTg, text);
            mountGatedResult(status, res, (ok) => {
              if (ok) rerender();
            });
          } catch {
            status.textContent = t("error.generic");
          }
        })();
      });

    const advBtn = container.querySelector<HTMLButtonElement>(".js-adv-run");
    if (advBtn && assignmentId) {
      advBtn.addEventListener("click", () => {
        const toPhase =
          container.querySelector<HTMLInputElement>(".js-adv-phase")!.value.trim() ||
          undefined;
        const status = container.querySelector<HTMLElement>(".js-adv-status")!;
        void (async () => {
          try {
            const res = await postAdvancePhase(assignmentId, toPhase);
            mountGatedResult(status, res, (ok) => {
              if (ok) rerender();
            });
          } catch {
            status.textContent = t("error.generic");
          }
        })();
      });
    }

    container.querySelector<HTMLButtonElement>(".js-note-save")!
      .addEventListener("click", () => {
        const content =
          container.querySelector<HTMLTextAreaElement>(".js-note-text")!.value.trim();
        const noteType =
          container.querySelector<HTMLSelectElement>(".js-note-type")!.value;
        const visibility =
          container.querySelector<HTMLSelectElement>(".js-note-visibility")!.value;
        const status = container.querySelector<HTMLElement>(".js-note-status")!;
        if (!content) {
          status.textContent = t("error.generic");
          return;
        }
        void (async () => {
          try {
            const res = await postCoachNote(clientTg, {
              content,
              note_type: noteType,
              visibility,
            });
            status.textContent =
              res.ok === true ? t("brief.note_ok") : t("error.generic");
            if (res.ok === true) {
              container.querySelector<HTMLTextAreaElement>(".js-note-text")!.value = "";
            }
          } catch {
            status.textContent = t("error.generic");
          }
        })();
      });

    container.querySelector<HTMLButtonElement>(".js-task-save")!
      .addEventListener("click", () => {
        const title =
          container.querySelector<HTMLInputElement>(".js-task-title")!.value.trim();
        const description =
          container.querySelector<HTMLInputElement>(".js-task-desc")!.value.trim();
        const dueKind =
          container.querySelector<HTMLSelectElement>(".js-task-due")!.value;
        const dateRaw =
          container.querySelector<HTMLInputElement>(".js-task-date")!.value;
        const status = container.querySelector<HTMLElement>(".js-task-status")!;
        if (!title || (dueKind === "date" && !dateRaw)) {
          status.textContent = t("error.generic");
          return;
        }
        const task: Record<string, unknown> = {
          kind: "task",
          title,
          due_kind: dueKind,
        };
        if (description) task.description = description;
        if (dueKind === "date" && dateRaw) {
          task.due_at = new Date(dateRaw).toISOString();
        }
        void (async () => {
          try {
            const res = await postCoachTasks(clientTg, [task]);
            status.textContent =
              res.ok === true ? t("brief.task_ok") : t("error.generic");
            if (res.ok === true) rerender();
          } catch {
            status.textContent = t("error.generic");
          }
        })();
      });

    const closeTask = (taskId: number, status: "done" | "cancelled") =>
      void (async () => {
        try {
          const res = await completeCoachTask(taskId, status);
          if (res.ok === true) rerender();
        } catch {
          /* вердикт виден по списку после обновления */
        }
      })();
    container
      .querySelectorAll<HTMLButtonElement>(".js-task-done")
      .forEach((b) =>
        b.addEventListener("click", () => closeTask(Number(b.dataset.task), "done")));
    container
      .querySelectorAll<HTMLButtonElement>(".js-task-cancel")
      .forEach((b) =>
        b.addEventListener("click", () =>
          closeTask(Number(b.dataset.task), "cancelled")));
  })();
}
